/**
 * ---------------------------------------------------------------------------------------------------------------------
 * BUNDLE SWITCHER
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Connects several *different* products (a "bundle" / product family) on the same product page. Clicking one of the
 * boxes swaps the whole product section (gallery, media, price, variant picker, buy buttons, sticky bar) for the other
 * product without a page reload, and updates the address bar.
 *
 * It relies on the Shopify Section Rendering API: `/products/<handle>?section_id=<id>` returns the markup of the
 * main product section rendered for that other product. All product templates of this theme use the section id `main`,
 * so the swap works across templates.
 *
 * Markup contract (see snippets/bundle-switcher.liquid):
 *
 *   <bundle-switcher section-id="main" current-url="/products/a">
 *     <a href="/products/b" data-bundle-url="/products/b">…</a>
 *   </bundle-switcher>
 */
(() => {
  if (window.customElements.get('bundle-switcher')) {
    return;
  }

  const CACHE_EVICTION_TIME = 1000 * 60 * 5;
  const cache = new Map();

  /**
   * Fetch (and memoize) the rendered section markup of another product
   */
  const fetchSection = (url, sectionId) => {
    const cacheKey = `${url}::${sectionId}`;
    const cached = cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < CACHE_EVICTION_TIME) {
      return cached.htmlPromise;
    }

    const sectionUrl = new URL(url, window.location.origin);
    sectionUrl.searchParams.set('section_id', sectionId);

    const htmlPromise = fetch(sectionUrl.toString())
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Bundle switcher: unable to load ${sectionUrl} (${response.status})`);
        }

        return response.text();
      })
      .catch((error) => {
        cache.delete(cacheKey);
        throw error;
      });

    cache.set(cacheKey, { htmlPromise, timestamp: Date.now() });

    if (cache.size > 50) {
      cache.delete(Array.from(cache.keys())[0]);
    }

    return htmlPromise;
  };

  /**
   * The browser history is only manipulated by us on this document, so any popstate happening while a switcher is
   * present means the visitor navigates back/forward between products that were swapped in.
   */
  let historyListenerAttached = false;

  const attachHistoryListener = () => {
    if (historyListenerAttached) {
      return;
    }

    historyListenerAttached = true;

    window.addEventListener('popstate', () => {
      const switcher = document.querySelector('bundle-switcher[section-id]');

      if (!switcher || switcher.getAttribute('current-url') === window.location.pathname) {
        return;
      }

      switcher.switchTo(window.location.href, { updateHistory: false });
    });
  };

  class BundleSwitcher extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();

      if (this.hasAttribute('disabled')) {
        return;
      }

      const { signal } = this._abortController;

      this.addEventListener('click', this._onClick.bind(this), { signal });
      this.addEventListener('pointerenter', this._onPreload.bind(this), { capture: true, signal });
      this.addEventListener('touchstart', this._onPreload.bind(this), { capture: true, passive: true, signal });

      attachHistoryListener();
      this._preloadWhenIdle();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    get sectionId() {
      return this.getAttribute('section-id');
    }

    get items() {
      return Array.from(this.querySelectorAll('[data-bundle-url]'));
    }

    /**
     * Replace the current product section by the one of the given product URL
     */
    async switchTo(url, { updateHistory = true, trigger = null } = {}) {
      const sectionId = this.sectionId;
      const currentSection = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;

      // A switch is already in progress: ignore the extra click instead of triggering a full page load
      if (this._isLoading) {
        return;
      }

      if (!currentSection) {
        window.location.href = url;
        return;
      }

      this._isLoading = true;
      this.classList.add('bundle-switcher--loading');
      trigger?.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const html = await fetchSection(url, sectionId);
        const fragment = document.createRange().createContextualFragment(html);
        const newSection =
          fragment.querySelector(`[id="shopify-section-${sectionId}"]`) ?? fragment.firstElementChild;

        if (!newSection) {
          throw new Error('Bundle switcher: the fetched document does not contain the product section');
        }

        // Keep the switcher visually anchored: the boxes should not move under the cursor while everything
        // above them (gallery, title, price...) changes height
        const anchorBefore = this.getBoundingClientRect().top;

        currentSection.replaceWith(newSection);

        const newSwitcher = newSection.querySelector('bundle-switcher') ?? newSection;
        const anchorAfter = newSwitcher.getBoundingClientRect().top;
        const delta = anchorAfter - anchorBefore;

        if (Math.abs(delta) > 1) {
          window.scrollBy({ top: delta, behavior: 'instant' });
        }

        if (updateHistory) {
          window.history.pushState({ bundleSwitcher: true, sectionId }, '', url);
        }

        window.Shopify?.PaymentButton?.init?.();

        newSection.dispatchEvent(
          new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId } })
        );

        document.dispatchEvent(
          new CustomEvent('bundle:switched', { bubbles: true, detail: { url, sectionId } })
        );
      } catch (error) {
        console.warn(error);
        window.location.href = url;
      } finally {
        this._isLoading = false;
        this.classList.remove('bundle-switcher--loading');
        trigger?.removeAttribute('aria-busy');
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    _onClick(event) {
      const trigger = event.target.closest('[data-bundle-url]');

      if (!trigger || !this.contains(trigger)) {
        return;
      }

      // Let the browser do its thing for "open in new tab", middle click...
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }

      event.preventDefault();

      if (trigger.hasAttribute('data-bundle-current')) {
        return;
      }

      this.switchTo(trigger.getAttribute('data-bundle-url'), { trigger });
    }

    _onPreload(event) {
      const trigger = event.target.closest?.('[data-bundle-url]:not([data-bundle-current])');

      if (trigger && this.contains(trigger)) {
        fetchSection(trigger.getAttribute('data-bundle-url'), this.sectionId).catch(() => {});
      }
    }

    /**
     * Prefetch all the other products of the bundle as soon as the browser is idle, so that the switch feels instant
     */
    _preloadWhenIdle() {
      const preloadAll = () => {
        this.items
          .filter((item) => !item.hasAttribute('data-bundle-current'))
          .forEach((item) => fetchSection(item.getAttribute('data-bundle-url'), this.sectionId).catch(() => {}));
      };

      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) {
          return;
        }

        observer.disconnect();

        if (window.requestIdleCallback) {
          window.requestIdleCallback(preloadAll, { timeout: 2000 });
        } else {
          setTimeout(preloadAll, 500);
        }
      });

      observer.observe(this);
      this._abortController.signal.addEventListener('abort', () => observer.disconnect());
    }
  }

  window.customElements.define('bundle-switcher', BundleSwitcher);
})();
